import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useCookieConsent } from '../../global/cookieConsent';

const updatedAt = '27 août 2026';

const LegalPage: React.FC<{title: string; children: React.ReactNode}> = ({ title, children }) => {
    useEffect(() => {
        const previousTitle = document.title;
        document.title = `${title} | FLAG`;
        return () => { document.title = previousTitle; };
    }, [title]);

    return (
        <main className="mx-2 my-6 max-w-4xl rounded-xl bg-white px-5 py-8 text-gray-700 sm:px-8 lg:mx-auto lg:my-[10vh] lg:px-12">
            <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
            <p className="mt-2 text-sm text-gray-500">Dernière mise à jour : {updatedAt}</p>
            <div className="mt-8 space-y-8 leading-7 [&_a]:text-primary [&_a]:underline [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-gray-900 [&_li]:ml-5 [&_li]:list-disc [&_p+p]:mt-3">
                {children}
            </div>
        </main>
    );
};

export const PrivacyPolicy = () => {
    const { openSettings } = useCookieConsent();

    return (
        <LegalPage title="Politique de confidentialité">
            <section>
                <h2>1. Responsable du traitement</h2>
                <p>
                    Field Legality Advisory Group (FLAG), association déclarée sous la référence
                    N°00001549/RDA/J06/DAPP, gère le site flag-cmr.org. FLAG est situé à Mfandena,
                    Rue Tsoungui Oloa (Titi garage), Immeuble La Grâce Divine, 2e étage, BP 4267,
                    Yaoundé, Cameroun.
                </p>
                <p>Pour toute question relative à vos données : <a href="mailto:mailflag@flag-cm.org">mailflag@flag-cm.org</a>.</p>
            </section>

            <section>
                <h2>2. Données collectées et finalités</h2>
                <ul className="space-y-2">
                    <li><strong>Formulaire de contact :</strong> nom, prénom, adresse e-mail, téléphone facultatif et message, afin de répondre à votre demande.</li>
                    <li><strong>Newsletter :</strong> adresse e-mail, afin de vous envoyer les actualités auxquelles vous avez choisi de vous abonner.</li>
                    <li><strong>Forum :</strong> nom, adresse e-mail et contributions. Si vous le demandez, le nom et l’e-mail peuvent être mémorisés localement dans votre navigateur.</li>
                    <li><strong>Administration :</strong> adresse e-mail et informations techniques d’authentification, afin de sécuriser l’accès aux fonctions réservées.</li>
                    <li><strong>Mesure d’audience :</strong> données techniques et événements de navigation transmis à Google Analytics uniquement après votre accord.</li>
                </ul>
                <p>Nous utilisons ces données pour fournir les services demandés, répondre aux messages, sécuriser le site, gérer les abonnements et, avec votre consentement, comprendre l’utilisation du site.</p>
            </section>

            <section>
                <h2>3. Cookies et technologies similaires</h2>
                <p>Les technologies nécessaires servent notamment à maintenir la sécurité, l’authentification et votre choix de consentement. Elles ne peuvent pas toutes être désactivées sans affecter les fonctions demandées.</p>
                <p>Google Analytics ainsi que les contenus intégrés Google Maps, Facebook et X sont désactivés jusqu’à votre acceptation. Vous pouvez refuser ces technologies sans perdre l’accès au contenu principal.</p>
                <button type="button" onClick={openSettings} className="mt-3 rounded-md border border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-green-50">
                    Modifier mon choix de cookies
                </button>
            </section>

            <section>
                <h2>4. Destinataires et prestataires</h2>
                <p>Les données sont accessibles aux membres de FLAG habilités selon leurs missions. Le site utilise les services Google Firebase pour la base de données, le stockage, l’authentification, l’hébergement technique associé et, après consentement, Google Analytics. Lorsque vous activez un contenu externe ou suivez un lien social, le fournisseur concerné traite les données selon sa propre politique.</p>
                <p>Certains prestataires peuvent traiter des données hors du Cameroun. FLAG veille à limiter les informations transmises à ce qui est nécessaire au service utilisé.</p>
            </section>

            <section>
                <h2>5. Durée de conservation et sécurité</h2>
                <p>Les données sont conservées pendant la durée nécessaire à la finalité concernée, puis supprimées ou archivées lorsque la loi ou la défense des droits de FLAG l’exige. Une demande de contact est conservée le temps de son traitement et de son suivi; une adresse de newsletter jusqu’au désabonnement; les comptes et contributions tant qu’ils sont nécessaires au fonctionnement et à la sécurité des espaces concernés.</p>
                <p>FLAG applique des mesures organisationnelles et techniques raisonnables pour protéger les données. Aucun système connecté ne pouvant offrir une sécurité absolue, nous vous invitons à ne pas transmettre d’informations sensibles non nécessaires.</p>
            </section>

            <section>
                <h2>6. Vos droits</h2>
                <p>Conformément à la <a href="https://www.prc.cm/files/2b/9f/21/1055fa3c2251b4f4248fd301f584daaf.pdf" target="_blank" rel="noreferrer">loi camerounaise n°2024/017 du 23 décembre 2024</a> relative à la protection des données à caractère personnel, vous pouvez demander l’accès, la rectification ou l’effacement de vos données, vous opposer à certains traitements, en demander la limitation ou retirer un consentement.</p>
                <p>Adressez votre demande à <a href="mailto:mailflag@flag-cm.org">mailflag@flag-cm.org</a> en précisant les informations permettant de vous identifier et la demande concernée. FLAG peut demander un justificatif lorsque cela est nécessaire pour éviter de communiquer des données à un tiers.</p>
            </section>

            <section>
                <h2>7. Évolution de cette politique</h2>
                <p>Cette politique peut être mise à jour pour refléter une évolution du site, des prestataires ou du cadre applicable. La date affichée en haut de page permet d’identifier la version en vigueur.</p>
                <p>Consultez également nos <Link to="/conditions-utilisation">conditions d’utilisation</Link>.</p>
            </section>
        </LegalPage>
    );
};

export const TermsOfUse = () => (
    <LegalPage title="Conditions d’utilisation">
        <section>
            <h2>1. Objet et éditeur</h2>
            <p>Les présentes conditions régissent l’accès et l’utilisation du site flag-cmr.org, édité par Field Legality Advisory Group (FLAG), association déclarée sous la référence N°00001549/RDA/J06/DAPP et établie à Yaoundé, Cameroun.</p>
            <p>En utilisant le site, vous acceptez ces conditions. Si vous ne les acceptez pas, veuillez ne pas utiliser les fonctionnalités interactives du site.</p>
        </section>

        <section>
            <h2>2. Contenus et propriété intellectuelle</h2>
            <p>Sauf indication contraire, la structure du site, les textes, visuels, vidéos, logos, documents et autres contenus publiés par FLAG sont protégés. Leur consultation et leur partage au moyen des liens prévus sont autorisés pour un usage licite et non trompeur.</p>
            <p>Toute reproduction, adaptation, extraction substantielle ou utilisation commerciale nécessite l’autorisation préalable du titulaire des droits, sauf exception prévue par la loi ou licence expressément indiquée sur le contenu.</p>
        </section>

        <section>
            <h2>3. Utilisation acceptable</h2>
            <p>Vous vous engagez à ne pas perturber le fonctionnement du site, contourner ses mesures de sécurité, accéder sans autorisation à ses espaces réservés, diffuser un contenu illégal ou malveillant, usurper l’identité d’un tiers, ni utiliser les informations publiées d’une manière portant atteinte aux droits d’autrui.</p>
        </section>

        <section>
            <h2>4. Contributions et forum</h2>
            <p>Vous restez responsable des messages et documents que vous publiez. Vous confirmez disposer des droits nécessaires et accordez à FLAG l’autorisation non exclusive de les afficher et de les reproduire dans la mesure nécessaire au fonctionnement du service.</p>
            <p>FLAG peut modérer ou retirer un contenu manifestement illicite, injurieux, trompeur, hors sujet, publicitaire ou portant atteinte aux droits d’un tiers, et peut restreindre un accès en cas d’abus.</p>
        </section>

        <section>
            <h2>5. Exactitude, disponibilité et liens externes</h2>
            <p>FLAG s’efforce de publier des informations utiles et à jour, sans garantir l’absence totale d’erreurs. Les informations du site ne remplacent pas un conseil juridique, technique ou professionnel adapté à une situation particulière.</p>
            <p>Le site peut être interrompu pour maintenance, sécurité ou raisons indépendantes de FLAG. Les liens et contenus externes sont fournis à titre pratique; FLAG ne contrôle pas leurs pratiques, leur disponibilité ni leur contenu.</p>
        </section>

        <section>
            <h2>6. Responsabilité</h2>
            <p>Dans les limites permises par la loi, FLAG ne saurait être responsable d’un dommage indirect résultant de l’utilisation du site, d’une indisponibilité temporaire, d’un contenu tiers ou d’une utilisation des informations contraire à leur contexte. Rien dans ces conditions n’exclut une responsabilité qui ne peut légalement être exclue.</p>
        </section>

        <section>
            <h2>7. Données personnelles</h2>
            <p>Le traitement des données personnelles et l’utilisation des cookies sont décrits dans la <Link to="/politique-de-confidentialite">politique de confidentialité</Link>.</p>
        </section>

        <section>
            <h2>8. Droit applicable, modifications et contact</h2>
            <p>Ces conditions sont régies par le droit camerounais. Les parties chercheront d’abord une solution amiable à tout différend, sans priver l’utilisateur des recours qui lui sont reconnus par la loi.</p>
            <p>FLAG peut modifier ces conditions afin de tenir compte de l’évolution du site ou du cadre applicable. Pour toute question : <a href="mailto:mailflag@flag-cm.org">mailflag@flag-cm.org</a>.</p>
        </section>
    </LegalPage>
);
